import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { I18nProvider } from "@/lib/i18n";

import Splash from "@/pages/Splash";
import Login from "@/pages/Login";
import Register from "@/pages/Register";

import CustomerLayout from "@/layouts/CustomerLayout";
import CustomerDashboard from "@/pages/customer/Dashboard";
import SavingWallet from "@/pages/customer/SavingWallet";
import BonusWallet from "@/pages/customer/BonusWallet";
import AddMoney from "@/pages/customer/AddMoney";
import Withdraw from "@/pages/customer/Withdraw";
import Policy from "@/pages/customer/Policy";
import Benefits from "@/pages/customer/Benefits";
import Referral from "@/pages/customer/Referral";
import LuckyDraw from "@/pages/customer/LuckyDraw";
import Notifications from "@/pages/customer/Notifications";
import Profile from "@/pages/customer/Profile";
import Transactions from "@/pages/customer/Transactions";
import FinanceSupport from "@/pages/customer/FinanceSupport";
import DailyCollection from "@/pages/customer/DailyCollection";

import AgentLayout from "@/layouts/AgentLayout";
import AgentDashboard from "@/pages/agent/Dashboard";
import AgentCustomers from "@/pages/agent/Customers";
import AgentAddCustomer from "@/pages/agent/AddCustomer";
import AgentCollections from "@/pages/agent/Collections";
import AgentEarnings from "@/pages/agent/Earnings";
import AgentPolicies from "@/pages/agent/Policies";
import AgentFinanceSupport from "@/pages/agent/FinanceSupport";

import AdminLayout from "@/layouts/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminCustomers from "@/pages/admin/Customers";
import AdminAgents from "@/pages/admin/Agents";
import AdminLuckyDraw from "@/pages/admin/LuckyDraw";
import AdminReports from "@/pages/admin/Reports";
import AdminFinance from "@/pages/admin/Finance";
import AdminPolicies from "@/pages/admin/Policies";
import AdminAgentEarnings from "@/pages/admin/AgentEarnings";
import AdminOtpWallet from "@/pages/admin/OtpWallet";
import AdminSubscription from "@/pages/admin/Subscription";
import AdminPaymentSettings from "@/pages/admin/PaymentSettings";
import BenefitSettings from "@/pages/admin/BenefitSettings";
import BenefitApprovals from "@/pages/admin/BenefitApprovals";
import AgentShareTracker from "@/pages/admin/AgentShareTracker";

import SuperAdminLayout from "@/layouts/SuperAdminLayout";
import SuperDashboard from "@/pages/super-admin/Dashboard";
import SuperTenants from "@/pages/super-admin/Tenants";
import SuperTenantDetail from "@/pages/super-admin/TenantDetail";
import SuperAdminSettings from "@/pages/super-admin/Settings";

const RequireAuth = ({ role, children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) {
    // Send user to their home
    return <Navigate to={roleHome(user.role)} replace />;
  }
  return children;
};

const roleHome = (role) => {
  switch (role) {
    case "super_admin": return "/super-admin";
    case "tenant_admin": return "/admin";
    case "agent": return "/agent";
    case "customer": return "/customer";
    default: return "/login";
  }
};

const RootRedirect = () => {
  const { user } = useAuth();
  if (user) return <Navigate to={roleHome(user.role)} replace />;
  return <Splash />;
};

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

            {/* Customer */}
            <Route path="/customer" element={<RequireAuth role="customer"><CustomerLayout /></RequireAuth>}>
              <Route index element={<CustomerDashboard />} />
              <Route path="wallet" element={<SavingWallet />} />
              <Route path="bonus" element={<BonusWallet />} />
              <Route path="add-money" element={<AddMoney />} />
              <Route path="withdraw" element={<Withdraw />} />
              <Route path="policy" element={<Policy />} />
              <Route path="benefits" element={<Benefits />} />
              <Route path="referral" element={<Referral />} />
              <Route path="lucky-draw" element={<LuckyDraw />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="profile" element={<Profile />} />
              <Route path="transactions" element={<Transactions />} />
              <Route path="finance-support" element={<FinanceSupport />} />
              <Route path="daily-collection" element={<DailyCollection />} />
            </Route>

            {/* Agent */}
            <Route path="/agent" element={<RequireAuth role="agent"><AgentLayout /></RequireAuth>}>
              <Route index element={<AgentDashboard />} />
              <Route path="customers" element={<AgentCustomers />} />
              <Route path="add-customer" element={<AgentAddCustomer />} />
              <Route path="policies" element={<AgentPolicies />} />
              <Route path="collections" element={<AgentCollections />} />
              <Route path="finance-support" element={<AgentFinanceSupport />} />
              <Route path="earnings" element={<AgentEarnings />} />
            </Route>

            {/* Tenant Admin */}
            <Route path="/admin" element={<RequireAuth role="tenant_admin"><AdminLayout /></RequireAuth>}>
              <Route index element={<AdminDashboard />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="agents" element={<AdminAgents />} />
              <Route path="agents/:id/earnings" element={<AdminAgentEarnings />} />
              <Route path="policies" element={<AdminPolicies />} />
              <Route path="lucky-draw" element={<AdminLuckyDraw />} />
              <Route path="finance" element={<AdminFinance />} />
              <Route path="benefits/settings" element={<BenefitSettings />} />
              <Route path="benefits/approvals" element={<BenefitApprovals />} />
              <Route path="benefits/agent-share" element={<AgentShareTracker />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="otp-wallet" element={<AdminOtpWallet />} />
              <Route path="subscription" element={<AdminSubscription />} />
              <Route path="payment-settings" element={<AdminPaymentSettings />} />
            </Route>

            {/* Super Admin */}
            <Route path="/super-admin" element={<RequireAuth role="super_admin"><SuperAdminLayout /></RequireAuth>}>
              <Route index element={<SuperDashboard />} />
              <Route path="tenants" element={<SuperTenants />} />
              <Route path="tenants/:id" element={<SuperTenantDetail />} />
              <Route path="settings" element={<SuperAdminSettings />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
